import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Truck,
  ClipboardList,
  Sparkles,
  LogOut,
  Wine,
  Shield,
  UserCog,
  ArrowLeft,
  AlertTriangle,
  Settings as SettingsIcon,
  ScanLine,
  Utensils,
} from "lucide-react";

const baseNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory" },
  { to: "/scan", label: "Scan", icon: ScanLine, testId: "nav-scan" },
  { to: "/sales", label: "Sales", icon: ShoppingCart, testId: "nav-sales" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testId: "nav-suppliers" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testId: "nav-pos" },
  { to: "/toast", label: "Toast POS", icon: Utensils, testId: "nav-toast" },
  { to: "/alerts", label: "Cost Alerts", icon: AlertTriangle, testId: "nav-alerts" },
  { to: "/ai-suggestions", label: "AI Insights", icon: Sparkles, testId: "nav-ai" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const isImpersonating = !!user?._impersonated_by;
  const isSuperadmin = user?.role === "superadmin";

  const nav = [
    ...baseNav,
    ...(isSuperadmin
      ? [{ to: "/admin", label: "Customers", icon: Shield, testId: "nav-admin" }]
      : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const stopImpersonating = async () => {
    try {
      await api.post("/admin/stop-impersonating");
      toast.success("Returned to admin");
      await refresh();
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0c10] text-slate-100 grain">
      {isImpersonating && (
        <div
          className="fixed top-0 left-0 right-0 z-40 bg-red-500/95 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm shadow-lg"
          data-testid="impersonation-banner"
        >
          <UserCog className="w-4 h-4" />
          <span>
            You are viewing as{" "}
            <span className="font-mono-plex font-semibold">{user.email}</span>
          </span>
          <button
            onClick={stopImpersonating}
            data-testid="stop-impersonating-btn"
            className="ml-2 px-3 py-1 rounded bg-white/20 hover:bg-white/30 text-xs flex items-center gap-1 font-medium"
          >
            <ArrowLeft className="w-3 h-3" /> Return to admin
          </button>
        </div>
      )}

      <aside
        className="fixed inset-y-0 left-0 w-64 border-r border-white/5 bg-[#0a0c10]/80 backdrop-blur-xl z-30 flex flex-col"
        style={{ paddingTop: isImpersonating ? "40px" : 0 }}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-white/5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-amber-500 flex items-center justify-center">
              <Wine className="w-5 h-5 text-black" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight">
                Barkeep<span className="text-amber-500">.</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex">
                {user?.bar_name || "your bar"}
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500"
                    : "text-slate-400 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-white/5">
          <div className="text-xs text-slate-500 mb-2 font-mono-plex">
            {user?.email}
            {isSuperadmin && (
              <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded">
                SA
              </span>
            )}
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/[0.04] transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main
        className="flex-1 ml-64 relative z-10"
        style={{ paddingTop: isImpersonating ? "40px" : 0 }}
      >
        <div className="max-w-7xl mx-auto px-8 py-10 page-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
